import { m, AnimatePresence } from 'framer-motion';
import { useMemo, useState, useEffect } from 'react';

import { Stack, useTheme, Typography } from '@mui/material';

import { useUserStore } from 'src/stores/user/user.store';

import Image from '../image';

export default function NotificationHeaderCard() {
  const theme = useTheme();
  const [userPointsNotifications] = useUserStore((state) => [state.userPointsNotifications]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    ['/assets/confetti.png', '/assets/medal-level-up.png'].forEach((src) => {
      const img = new window.Image();
      img.src = src;
    });
  }, []);

  useEffect(() => {
    const notificationType = userPointsNotifications?.notificationData?.type;
    const shouldShow =
      userPointsNotifications?.notify &&
      (notificationType === 'pointsNotification' || notificationType === 'tierNotification');

    if (shouldShow) {
      setIsVisible(true);
      const timer = window.setTimeout(() => {
        setIsVisible(false);
      }, 5000);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [userPointsNotifications?.notify, userPointsNotifications?.notificationData?.type]);

  const translateTier = (tier?: string): string => {
    const translations: Record<string, string> = {
      silver: 'Prata',
      gold: 'Ouro',
      diamond: 'Diamante',
    };
    return translations[tier?.toLowerCase() ?? ''] ?? tier ?? '';
  };

  const PointsNotification = (
    <Stack direction="row" spacing={4} alignItems="center" justifyContent="center">
      <Image
        src="/assets/confetti.png"
        sx={{ width: 120, height: 120, objectFit: 'contain', flexShrink: 0 }}
      />
      <Stack spacing={4}>
        <Typography
          variant="labelL"
          color={theme.palette.redBrand[300]}
        >
          Mandou bem!
          <br />
          Você ganhou {userPointsNotifications?.notificationData?.validatedPoints} pontos.
        </Typography>
        <Typography
          variant="paragraphP500"
          color={theme.palette.blueBrand[400]}
        >
          Continue escaneando suas notas fiscais e desbloqueie as vantagens do{' '}
          <b>nível {translateTier(userPointsNotifications?.notificationData?.nextTier)}.</b>
        </Typography>
      </Stack>
    </Stack>
  );

  const TierNotification = (
    <Stack direction="row" spacing={12} alignItems="center" justifyContent="center">
      <Image
        src="/assets/medal-level-up.png"
        sx={{ width: 40 }}
      />
      <Typography variant="labelM" color={theme.palette.blueBrand[400]}>
        Parabéns! Você avançou de nível e têm novos benefícios.
      </Typography>
    </Stack>
  );

  const content = useMemo(() => {
    const notificationType = userPointsNotifications?.notificationData?.type;
    if (notificationType === 'pointsNotification') return PointsNotification;
    if (notificationType === 'tierNotification') return TierNotification;
    return null;
  }, [userPointsNotifications?.notificationData?.type]);

  const easing = [0.25, 0.46, 0.45, 0.94] as [number, number, number, number];

  return (
    <m.div
      animate={{
        marginInline: isVisible ? theme.spacing(12) : theme.spacing(0),
        marginBlock: isVisible ? theme.spacing(6) : theme.spacing(0),
      }}
      transition={{ duration: 0.5, ease: easing }}
    >
      <AnimatePresence initial={false}>
        {isVisible && (
          <m.div
            key="notif-card"
            initial={{ height: 0, opacity: 0, scale: 0.98 }}
            animate={{ height: 'auto', opacity: 1, scale: 1 }}
            exit={{ height: 0, opacity: 0, scale: 0.98 }}
            transition={{
              height: { duration: 0.5, ease: easing },
              opacity: { duration: 0.3, ease: 'easeOut' },
              scale: { duration: 0.3, ease: 'easeOut' },
            }}
            style={{ overflow: 'hidden' }}
          >
            <Stack
              p={8}
              borderRadius={6}
              bgcolor={theme.palette.blueBrand[0]}
              spacing={4}
              sx={{
                '& > *': { willChange: 'opacity, transform' },
              }}
              component={m.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
            >
              {content}
            </Stack>
          </m.div>
        )}
      </AnimatePresence>
    </m.div>
  );
}
